import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface Founder {
  id: number;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}

interface Pitch {
  id: number;
  startupName: string;
  shortDescription: string;
  fullDescription: string;
  fundingAmount: number;
  companyName?: string;
  companyWebsite?: string;
  labels: string[];
  isPublic: boolean;
  isFunded: boolean;
  createdAt: string;
  founder: Founder;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ApiResponse {
  pitches: Pitch[];
  pagination: Pagination;
}

const HomePage: React.FC = () => {
  const { themeClasses, isLightTheme } = useTheme();
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLabel] = useState<string>('');
  const [selectedFilter] = useState<'all' | 'my-posts'>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  // const labels = ['Super', 'Alpha', 'Group A'];

  useEffect(() => {
    fetchPitches();
  }, [selectedLabel, selectedFilter, page]);

  const fetchPitches = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 10,
        ...(selectedLabel && { label: selectedLabel })
      };

      // Add filter for my posts if selected
      if (selectedFilter === 'my-posts') {
        params.myPosts = true;
      }
      
      const response = await api.get<ApiResponse>('/pitches', { params });
      const { pitches: newPitches, pagination } = response.data;
      
      if (page === 1) {
        setPitches(newPitches);
      } else {
        setPitches(prev => [...prev, ...newPitches]);
      }
      
      setHasMore(pagination.hasNext);
    } catch (error) {
      console.error('Error fetching pitches:', error);
      toast.error('Failed to load pitches');
    } finally {
      setLoading(false);
    }
  };

  // const handleFilterChange = (filter: 'all' | 'my-posts') => {
  //   setSelectedFilter(filter);
  //   setPage(1);
  //   setPitches([]);
  // };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handlePitchClick = (pitchId: number) => {
    navigate(`/pitch/${pitchId}`);
  };

  return (
    <Layout>
      {/* Top list indicator */}
      <div className={`${themeClasses.card} border-b ${themeClasses.border} p-4 mb-4`}>
        <div className="flex items-center">
          <TrendingUp className={`w-5 h-5 ${themeClasses.textSecondary} mr-2`} />
          <span className={`${themeClasses.textSecondary} text-sm`}>Top list</span>
        </div>
      </div>

      {/* Filter Buttons for Founders */}
      {/* {user?.accountType === 'founder' && (
        <div className={`${themeClasses.card} border-b ${themeClasses.border} p-4`}>
          <div className="flex space-x-2">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === 'all'
                  ? `${themeClasses.button}`
                  : `${themeClasses.buttonSecondary}`
              }`}
            >
              All Posts
            </button>
            <button
              onClick={() => handleFilterChange('my-posts')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === 'my-posts'
                  ? `${themeClasses.button}`
                  : `${themeClasses.buttonSecondary}`
              }`}
            >
              My Posts
            </button>
          </div>
        </div>
      )} */}

      {/* Pitches List */}
      <div className="p-4 space-y-4">
        {pitches.map((pitch) => (
          <div 
            key={pitch.id} 
            className={`${themeClasses.card} rounded-lg cursor-pointer hover:${isLightTheme ? 'shadow-[0_12px_50px_rgb(201,227,225,0.9)] scale-[1.02]' : 'bg-gray-750'} transition-all duration-300`}
            onClick={() => handlePitchClick(pitch.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-20 h-20 ${isLightTheme ? 'bg-gradient-to-r from-cyan-100 to-blue-100' : 'bg-gray-600'} rounded-full flex items-center justify-center mr-3 shadow-sm`}>
                  {pitch.founder.profilePictureUrl ? (
                    <img
                      src={pitch.founder.profilePictureUrl}
                      alt={`${pitch.founder.firstName} ${pitch.founder.lastName}`}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <User className={`w-5 h-5 ${themeClasses.textSecondary}`} />
                  )}
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{pitch.startupName}</h3>
                  <p className={`${themeClasses.textSecondary} text-sm`}>
                    by {pitch.founder.firstName} {pitch.founder.lastName}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-semibold ${isLightTheme ? 'text-cyan-600' : 'text-primary-400'}`}>
                  {formatCurrency(pitch.fundingAmount)}
                </p>
                <p className={`${themeClasses.textSecondary} text-sm`}>Funding Goal</p>
              </div>
            </div>

            <p className={`${themeClasses.textSecondary} mb-4`}>{pitch.shortDescription}</p>

            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                {['Alpha'].map((label) => (
                  <span
                    key={label}
                    className={`px-3 py-1 ${isLightTheme ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 border border-cyan-200' : 'bg-gray-700 text-gray-300'} text-xs rounded-full font-medium shadow-sm`}
                  >
                    {label}
                  </span>
                ))}
              </div>
              {pitch.isFunded && (
                <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs rounded-full font-medium shadow-sm">
                  Funded
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center">
            <button
              onClick={loadMore}
              disabled={loading}
              className={`${themeClasses.buttonSecondary} px-6 py-3 font-medium rounded-xl transition-all duration-300`}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && pitches.length === 0 && (
          <div className="text-center py-12">
            <p className={themeClasses.textSecondary}>No pitches found</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HomePage; 